import { Button } from '@/components/ui/Button'
import { Icone } from '@/components/ui/Icone'

/**
 * Arrêter une épreuve en cours — [DECISIONS §178], RG-3.20.
 *
 * **Il n'y avait aucune sortie.** Une fois le quiz commencé, le seul chemin
 * était de répondre à tout : le candidat qu'on appelle, ou qui s'est trompé
 * d'écran, n'avait que le rechargement de la page — un geste hors du produit.
 *
 * Un enfermement ne rend pas les réponses plus sincères ; il fait fermer
 * l'onglet, et ce qui revient alors est un candidat qui a perdu son travail
 * sans comprendre pourquoi.
 */

/**
 * Le bouton, **discret et à sa place** — c'est une sortie de secours, pas une
 * invitation à renoncer à la deuxième question. D'où l'icône seule et le nom
 * accessible : dans un en-tête d'épreuve, le rang et le chronomètre passent
 * d'abord.
 */
export function BoutonArreter({ onArreter }: { onArreter: () => void }) {
  return (
    <button
      type="button"
      onClick={onArreter}
      aria-label="Arrêter l’épreuve"
      className="flex size-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-[var(--surface-2)]"
      style={{ color: 'var(--texte-2)' }}
    >
      <Icone nom="croix" taille={18} />
    </button>
  )
}

/**
 * La confirmation — §178.
 *
 * **Le message n'est pas le même selon l'épreuve, parce que la vérité ne l'est
 * pas.** Dans une épreuve à remise groupée (RG-3.11octies), rien n'a quitté
 * l'appareil : arrêter jette ce qui est à l'écran. Dans un quiz chronométré,
 * chaque réponse est déjà partie et compte dans l'objectif du jour : dire
 * « tes réponses ne seront pas enregistrées » y serait un mensonge que le
 * compteur démentirait dans la seconde.
 */
export function ConfirmerLArret({
  /** Ce qui a déjà été composé, pour ne pas parler au conditionnel. */
  repondues,
  total,
  /**
   * Les réponses sont-elles DÉJÀ parties ? Vrai pour le quiz chronométré, qui
   * poste au fil de l'eau ; faux pour les épreuves à remise groupée.
   */
  dejaComptees,
  onAnnuler,
  onConfirmer,
}: {
  repondues: number
  total: number
  dejaComptees: boolean
  onAnnuler: () => void
  onConfirmer: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onAnnuler}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label="Arrêter l’épreuve"
        // **La feuille s'arrête AU-DESSUS de la barre et du bouton rond** —
        // [DECISIONS §298]. `fixed inset-0` se résout contre le viewport de MISE
        // EN PAGE, qui reste à taille pleine pendant que la barre d'adresse du
        // navigateur mange le bas de l'écran : le bas de la feuille tombe alors
        // hors de vue, et la barre — collée au viewport RÉELLEMENT visible —
        // recouvre ce qu'il en reste. Les 85 px sont la mesure de la feuille du
        // bouton « + », qui avait rencontré ce défaut la première.
        //
        // `dvh` et non `vh` : `vh` se résout aussi contre le viewport de mise
        // en page, et se croit donc plus de place qu'il n'y en a.
        className="flex w-full max-w-lg flex-col gap-3 overflow-y-auto overscroll-contain rounded-t-3xl p-4"
        style={{
          background: 'var(--fond)',
          paddingBottom: 'calc(85px + env(safe-area-inset-bottom))',
          maxHeight: '100dvh',
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <h2 className="text-[20px] font-extrabold">Arrêter l’épreuve ?</h2>

        <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
          {dejaComptees ? (
            <>
              Tu as répondu à {repondues} question{repondues > 1 ? 's' : ''} sur{' '}
              {total}. <strong style={{ color: 'var(--texte-1)' }}>Ce à quoi tu as
              déjà répondu reste compté</strong> ; les questions restantes seront
              abandonnées.
            </>
          ) : repondues === 0 ? (
            <>
              Tu n’as encore répondu à aucune question — il n’y a rien à perdre.
            </>
          ) : (
            <>
              Tu as répondu à {repondues} question{repondues > 1 ? 's' : ''} sur{' '}
              {total}, et rien n’a encore été rendu.{' '}
              <strong style={{ color: 'var(--texte-1)' }}>
                Ces réponses ne seront pas enregistrées.
              </strong>
            </>
          )}
        </p>

        {/* **Continuer d'abord.** Le bouton par défaut d'une feuille de
            confirmation est celui qui ne détruit rien : on ouvre cette feuille
            en hésitant, pas en ayant décidé. */}
        <Button onClick={onAnnuler}>Continuer l’épreuve</Button>

        <Button variant="outline" onClick={onConfirmer}>
          Arrêter et revenir
        </Button>
      </div>
    </div>
  )
}
